import { ComponentType, FocusEvent, forwardRef, useRef, useState } from 'react';
import { Status } from '@optimus-web/common';
import { IconName } from '@optimus-web/icons';
import { BareIconButton } from '../BareIconButton';
import { FormLabel } from '../common/FormLabel';
import { Stack } from '../Stack';
import { FieldFormMessages } from './FieldFormMessages';
import { FieldHeaderProps, FieldProps } from './FieldProps';
import { FieldContainer, FieldHeader, FieldIconButtonWrapper, FieldInputWrapper } from './style';

export const FieldHeaderComponent = ({
    captionIconHandler,
    captionIconWithTooltip,
    $disabled,
    fieldWrapperRef,
    inline,
    label,
    name,
    readOnly,
    required,
    size,
}: FieldHeaderProps) => {
    const handleLabelClick = () => {
        if (!fieldWrapperRef.current) return;

        const focusableNode: HTMLElement | null = fieldWrapperRef.current.querySelector(
            'input, textarea, [tabindex]:not([tabindex="-1"])'
        );

        const dropzoneNode: HTMLDivElement | null = fieldWrapperRef.current.querySelector('[data-test-dropzone]');

        if (focusableNode) {
            focusableNode.focus();
        }

        if (dropzoneNode) {
            dropzoneNode.click();
        }
    };

    return captionIconHandler || captionIconWithTooltip || !!label ? (
        <FieldHeader size={size} readOnly={readOnly} $disabled={$disabled}>
            <FormLabel
                $disabled={$disabled}
                readOnly={readOnly}
                required={required}
                onClick={handleLabelClick}
                htmlFor={name}
                inline={inline}
            >
                {label}
            </FormLabel>
            <Stack spacing="50">
                {captionIconHandler ? (
                    <FieldIconButtonWrapper>
                        <BareIconButton
                            disabled={$disabled}
                            readOnly={readOnly}
                            icon={IconName.Help}
                            size="small"
                            onClick={captionIconHandler}
                            data-test-button-caption
                        />
                    </FieldIconButtonWrapper>
                ) : null}
                <FieldIconButtonWrapper>{captionIconWithTooltip}</FieldIconButtonWrapper>
            </Stack>
        </FieldHeader>
    ) : null;
};

const withField = <P extends object>(Component: ComponentType<P>) => {
    const Field = ({
        captionIconHandler,
        captionIconWithTooltip,
        className,
        disabled,
        error,
        forwardedRef,
        helper,
        inline,
        insideCompoundField,
        label,
        maxCharacterCount,
        name,
        onFocus,
        onBlur,
        readOnly,
        required,
        size,
        ...other
    }: FieldProps) => {
        const [focused, setFocused] = useState(false);
        const [inputLength, setInputLength] = useState(0);
        const fieldWrapperRef = useRef<HTMLDivElement>(null);

        const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
            setFocused(true);

            if (onFocus) {
                onFocus(event);
            }
        };

        const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
            setFocused(false);

            if (onBlur) {
                onBlur(event);
            }
        };

        let status;
        if (error) {
            status = Status.Error;
        }

        const getValueLength = (value: string) => {
            if (maxCharacterCount && value.length !== inputLength) {
                setInputLength(value.length);
            }
        };

        return (
            <FieldContainer ref={fieldWrapperRef} className={className} data-test-field={name}>
                {insideCompoundField || inline ? null : (
                    <FieldHeaderComponent
                        $disabled={disabled}
                        label={label}
                        required={required}
                        captionIconWithTooltip={captionIconWithTooltip}
                        captionIconHandler={captionIconHandler}
                        fieldWrapperRef={fieldWrapperRef}
                        name={name}
                        inline={inline}
                        size={size}
                        readOnly={readOnly}
                    />
                )}
                <FieldInputWrapper
                    readOnly={readOnly}
                    error={!!error}
                    focused={focused}
                    size={size}
                    status={status}
                    $disabled={disabled}
                >
                    <Component
                        name={name}
                        disabled={disabled}
                        required={required}
                        ref={forwardedRef}
                        size={size}
                        maxCharacterCount={maxCharacterCount}
                        getValue={getValueLength}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        inline={inline}
                        error={error}
                        readOnly={readOnly}
                        helper={helper}
                        captionIconWithTooltip={captionIconWithTooltip}
                        captionIconHandler={captionIconHandler}
                        insideCompoundField={insideCompoundField}
                        title={inline && helper ? helper : undefined}
                        {...(other as P)}
                    />
                </FieldInputWrapper>
                {!inline ? (
                    <FieldFormMessages
                        name={name}
                        error={error}
                        helper={helper}
                        disabled={disabled}
                        readOnly={readOnly}
                        size={size}
                        maxCharacterCount={maxCharacterCount}
                        inputLength={inputLength}
                    />
                ) : null}
            </FieldContainer>
        );
    };

    return forwardRef<HTMLElement, FieldProps & P>((props, ref) => (
        <Field {...props} forwardedRef={props.forwardedRef ?? ref} />
    ));
};

export default withField;
